import {
	Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
	FormControl, InputLabel, ListItemText, MenuItem, Select, Stack, TextField
} from "@mui/material";
import { addGroup } from "api/layer";
import { AppAction, AppContext } from "helpers/context";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./add-modal.module.scss";

interface Props {
	open: boolean;
	onClose: () => void;
	onSave: () => void;
	data?: FormData;
	endPointData: any[];
}

interface FormData {
	id?: string;
	groupName: string;
	endpointIds: any;
	remark: string;
}

function AddModal(props: Props) {
	const [t] = useTranslation();
	const [context, dispatch] = useContext(AppContext);
	const [formData, setFormData] = useState<FormData>(
		props.data
			? {
				...props.data,
				endpointIds: (() => {
					if (!props.data.endpointIds) return [];
					if (typeof props.data.endpointIds === 'string') {
						return props.data.endpointIds.split(',');
					}
					return Array.isArray(props.data.endpointIds) ? props.data.endpointIds : [];
				})()
			}
			: {
				groupName: "",
				endpointIds: [],
				remark: ""
			}
	);
	const [isError, setIsError] = useState(false);

	const handleChange = (event: any) => {
		const { name, value, type, checked } = event.target;
		const val = type === "checkbox" ? checked : value;
		if (name === "endpointIds") {
			setFormData((prevData) => ({ ...prevData, [name]: value === 'string' ? value.split(',') : value }));
		} else {
			setFormData((prevData) => ({ ...prevData, [name]: val }));
		}
	};

	const close = () => {
		props.onClose();
	};

	const handleSave = () => {
		let data = { ...formData };
		if (!data.groupName || !data.endpointIds) {
			setIsError(true);
			return;
		}
		setIsError(false);

		if (props.data) {
			addGroup(data)
				.then((res: any) => {
					if (res && res.code === 200) {
						props.onClose();
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "editEndPointGroup",
								message: "editEndPointGroupSuccess",
							},
						});
					} else {
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "editEndPointGroup",
								titleIconColor: "yellow",
								titleIcon: "warning",
								resMessage: res.message,
							},
						});
					}
				})
				.finally(() => {
					props.onSave();
				});
		} else {
			addGroup(data)
				.then((res: any) => {
					if (res && res.code === 200) {
						props.onClose();
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "addEndPointGroup",
								message: "addEndPointGroupSuccess",
							},
						});
					} else {
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "addEndPointGroup",
								titleIconColor: "yellow",
								titleIcon: "warning",
								resMessage: res.message,
							},
						});
					}
				})
				.finally(() => {
					props.onSave();
				});
		}
	};

	return (
		<Dialog
			open={props.open}
			onClose={close}
		>
			<DialogTitle
				sx={{
					fontWeight: 600,
					fontSize: "16px",
					opacity: 0.79,
					color: "#333",
					width: "400px",
				}}
			>
				{props.data ? t("userSystem.editEndPointGroup") : t("userSystem.addEndPointGroup")}
			</DialogTitle>
			<DialogContent sx={{ paddingTop: "10px!important" }}>
				<Stack spacing={{ xy: 1, sm: 2 }}>
					<TextField
						required
						name="groupName"
						value={formData.groupName}
						error={isError && !formData.groupName}
						onChange={handleChange}
						label={t("userSystem.groupName")}
						variant="outlined"
						color="success"
					/>

					<FormControl fullWidth error={isError && !formData.endpointIds}>
						<InputLabel color="success" required>{t("userSystem.endPoint")}</InputLabel>
						<Select
							name="endpointIds"
							value={formData.endpointIds}
							multiple
							onChange={handleChange}
							label={t("userSystem.endPoint")}
							renderValue={(selected) => props.endPointData.filter((item: any) => selected.includes(item.id))
								.map(item => item.name).join(',')}
							color="success"
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 350,
									},
								},
							}}
						>
							{props.endPointData.map((item) => (<MenuItem value={item.id} key={item.id}>{item.name}</MenuItem>))}
						</Select>
					</FormControl>

					<TextField
						name="remark"
						value={formData.remark}
						onChange={handleChange}
						label={t("userSystem.description")}
						variant="outlined"
						color="success"
					/>

				</Stack>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={close}
					color="success"
				>
					{t("buttons.cancel")}
				</Button>
				<Button
					onClick={handleSave}
					color="success"
					variant="contained"
				>
					{t("buttons.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default AddModal;
